import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getTeamDomesticTourismAgreementBlocks,
  type AgreementBlock
} from "../data/teamDomesticTourismAgreement";
import type { TravelDetailData } from "../types/travelDetail";
import { buildHeroMetaItems } from "../utils/travelMeta";

type BookingPageProps = {
  travel: TravelDetailData;
  apiBaseUrl: string;
  turnstileSiteKey?: string;
  paypalClientId?: string;
  onBack?: () => void;
  onPaymentResolved?: (result: BookingPaymentResult) => void;
};

export type BookingPaymentResult = {
  orderId: string;
  orderStatus: string;
  captureId: string;
  captureStatus: string;
  paymentState: "COMPLETED" | "PENDING";
  message: string;
};

const phoneCountryOptions = [
  ["US", "+1"],
  ["CA", "+1"],
  ["MX", "+52"],
  ["BR", "+55"],
  ["AR", "+54"],
  ["CO", "+57"],
  ["PE", "+51"],
  ["CL", "+56"],
  ["UK", "+44"],
  ["IE", "+353"],
  ["FR", "+33"],
  ["ES", "+34"],
  ["IT", "+39"],
  ["DE", "+49"],
  ["NL", "+31"],
  ["BE", "+32"],
  ["CH", "+41"],
  ["AT", "+43"],
  ["SE", "+46"],
  ["NO", "+47"],
  ["DK", "+45"],
  ["FI", "+358"],
  ["PL", "+48"],
  ["CZ", "+420"],
  ["GR", "+30"],
  ["TR", "+90"],
  ["KZ", "+7"],
  ["RU", "+7"],
  ["EG", "+20"],
  ["ZA", "+27"],
  ["AE", "+971"],
  ["SA", "+966"],
  ["IL", "+972"],
  ["IN", "+91"],
  ["PK", "+92"],
  ["BD", "+880"],
  ["JP", "+81"],
  ["KR", "+82"],
  ["CN", "+86"],
  ["HK", "+852"],
  ["MO", "+853"],
  ["TW", "+886"],
  ["PH", "+63"],
  ["MY", "+60"],
  ["SG", "+65"],
  ["TH", "+66"],
  ["VN", "+84"],
  ["ID", "+62"],
  ["AU", "+61"],
  ["NZ", "+64"]
] as const;

const TURNSTILE_ACTION = "booking_email_code";

type TurnstileRenderOptions = {
  sitekey: string;
  action?: string;
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
};

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: TurnstileRenderOptions) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

export default function BookingPage({
  travel,
  apiBaseUrl,
  turnstileSiteKey,
  paypalClientId,
  onBack
}: BookingPageProps) {
  const [fullName, setFullName] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("+1");
  const [phone, setPhone] = useState("");
  const [whatsApp, setWhatsApp] = useState("");
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sendCodeError, setSendCodeError] = useState<string | null>(null);
  const [sendCodeSuccess, setSendCodeSuccess] = useState<string | null>(null);
  const [sendCodePending, setSendCodePending] = useState(false);
  const [verifyCodePending, setVerifyCodePending] = useState(false);
  const [verifyCodeError, setVerifyCodeError] = useState<string | null>(null);
  const [verifyCodeSuccess, setVerifyCodeSuccess] = useState<string | null>(null);
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileVisible, setTurnstileVisible] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentPending, setPaymentPending] = useState(false);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [agreed, setAgreed] = useState(false);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  const metaItems = travel.sections.overview?.enabled ? buildHeroMetaItems(travel) : [];
  const bookingCard = travel.sections.bookingCard;
  const depositAmountLabel = travel.basic.depositAmount ?? travel.basic.priceFrom;
  const normalizedPayPalClientId = (paypalClientId || "").trim();
  const paypalEnabled = normalizedPayPalClientId.length > 0;

  const isEmailValid = useMemo(() => /^\S+@\S+\.\S+$/.test(email), [email]);
  const isCodeValid = verificationCode.trim().length === 6;
  const canAgreeToAgreement = fullName.trim().length > 0 && isEmailValid;
  const bookingAgreementBlocks = useMemo(
    () => getTeamDomesticTourismAgreementBlocks(fullName, email),
    [email, fullName]
  );

  const resetCodeVerificationState = useCallback(() => {
    setIsCodeVerified(false);
    setVerificationToken("");
    setVerifyCodeError(null);
    setVerifyCodeSuccess(null);
  }, []);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const timer = window.setTimeout(() => {
      setCooldown((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (!canAgreeToAgreement && agreed) {
      setAgreed(false);
    }
  }, [agreed, canAgreeToAgreement]);

  useEffect(() => {
    if (!turnstileSiteKey) {
      return;
    }

    if (window.turnstile) {
      setTurnstileReady(true);
      return;
    }

    const existed = document.querySelector<HTMLScriptElement>(
      'script[data-turnstile-script="true"]'
    );

    const onLoaded = () => {
      setTurnstileReady(true);
    };

    if (existed) {
      existed.addEventListener("load", onLoaded);
      if (window.turnstile) {
        setTurnstileReady(true);
      }
      return () => {
        existed.removeEventListener("load", onLoaded);
      };
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.setAttribute("data-turnstile-script", "true");
    script.addEventListener("load", onLoaded);
    document.head.appendChild(script);

    return () => {
      script.removeEventListener("load", onLoaded);
    };
  }, [turnstileSiteKey]);

  const sendCodeByToken = useCallback(async (captchaToken: string) => {
    setSendCodePending(true);
    setSendCodeError(null);
    setSendCodeSuccess(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/public/booking/send-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: email.trim(),
          turnstileToken: captchaToken
        })
      });

      const payload = (await response
        .json()
        .catch(() => null)) as
        | {
            success?: boolean;
            data?: { message?: string; cooldownSeconds?: number };
            error?: string;
          }
        | null;

      if (!response.ok || !payload?.success || !payload.data) {
        throw new Error(payload?.error || "Failed to send verification code.");
      }

      setCodeSent(true);
      setCooldown(Math.max(1, payload.data.cooldownSeconds ?? 60));
      setSendCodeSuccess(
        payload.data.message || "Verification code sent. Please check your inbox."
      );
    } catch (error) {
      setSendCodeError(
        error instanceof Error ? error.message : "Failed to send verification code."
      );
    } finally {
      setSendCodePending(false);
      if (window.turnstile && turnstileWidgetIdRef.current) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
      }
      turnstileWidgetIdRef.current = null;
    }
  }, [apiBaseUrl, email]);

  useEffect(() => {
    if (!turnstileVisible) {
      if (window.turnstile && turnstileWidgetIdRef.current) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
      }
      turnstileWidgetIdRef.current = null;
      return;
    }

    if (!turnstileSiteKey || !turnstileReady || !window.turnstile) {
      return;
    }

    if (turnstileWidgetIdRef.current) {
      return;
    }

    const widgetId = window.turnstile.render("#booking-turnstile", {
      sitekey: turnstileSiteKey,
      action: TURNSTILE_ACTION,
      callback: (token: string) => {
        setTurnstileVisible(false);
        void sendCodeByToken(token);
      },
      "expired-callback": () => {
        setSendCodeError("Verification expired. Please try again.");
      },
      "error-callback": () => {
        setSendCodeError("Bot verification failed to load. Please refresh and try again.");
      }
    });

    turnstileWidgetIdRef.current = widgetId;
  }, [sendCodeByToken, turnstileReady, turnstileSiteKey, turnstileVisible]);

  const handleSendCode = () => {
    if (isCodeVerified) {
      setSendCodeError(null);
      setSendCodeSuccess("Email is already verified. You can proceed to payment.");
      return;
    }

    if (!isEmailValid) {
      setSendCodeError("Please enter a valid email address first.");
      setSendCodeSuccess(null);
      return;
    }

    if (sendCodePending || cooldown > 0) {
      return;
    }

    if (!turnstileSiteKey) {
      setSendCodeError("Bot verification is not configured.");
      setSendCodeSuccess(null);
      return;
    }

    setSendCodeError(null);
    setSendCodeSuccess(null);
    resetCodeVerificationState();
    setTurnstileVisible(true);
  };

  const handleVerifyCode = async () => {
    if (isCodeVerified) {
      setVerifyCodeError(null);
      setVerifyCodeSuccess("Email is already verified. You can proceed to payment.");
      return;
    }

    if (!isEmailValid) {
      setVerifyCodeError("Please enter a valid email address first.");
      return;
    }

    if (!isCodeValid) {
      setVerifyCodeError("Please enter the valid 6-digit verification code.");
      return;
    }

    setVerifyCodePending(true);
    setVerifyCodeError(null);
    setVerifyCodeSuccess(null);
    setPaymentError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/api/public/booking/verify-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: email.trim(),
          verificationCode: verificationCode.trim()
        })
      });

      const payload = (await response
        .json()
        .catch(() => null)) as
        | {
            success?: boolean;
            data?: { verificationToken?: string; message?: string };
            error?: string;
          }
        | null;

      if (!response.ok || !payload?.success || !payload.data?.verificationToken) {
        throw new Error(payload?.error || "Failed to verify email code.");
      }

      setVerificationToken(payload.data.verificationToken);
      setIsCodeVerified(true);
      setVerifyCodeSuccess(
        payload.data.message || "Email verified. You can proceed to payment."
      );
    } catch (error) {
      setIsCodeVerified(false);
      setVerificationToken("");
      setVerifyCodeError(
        error instanceof Error ? error.message : "Failed to verify email code."
      );
    } finally {
      setVerifyCodePending(false);
    }
  };

  const isFormValid =
    fullName.trim().length > 0 &&
    phone.trim().length > 0 &&
    isEmailValid &&
    isCodeVerified &&
    verificationToken.trim().length > 0 &&
    agreed;

  const createPayPalOrder = useCallback(async () => {
    setPaymentError(null);
    setPaymentPending(true);
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/public/booking/paypal/create-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            travelId: travel.id,
            fullName: fullName.trim(),
            phoneCountryCode: phoneCountry,
            phone: phone.trim(),
            whatsApp: whatsApp.trim(),
            email: email.trim(),
            verificationToken: verificationToken.trim(),
            adults,
            children,
            startDate: startDate || null,
            endDate: endDate || null
          })
        }
      );

      const payload = (await response
        .json()
        .catch(() => null)) as
        | {
            success?: boolean;
            data?: { orderId?: string; approveUrl?: string };
            error?: string;
          }
        | null;

      if (!response.ok || !payload?.success || !payload.data?.approveUrl) {
        throw new Error(payload?.error || "Failed to create PayPal order.");
      }

      return payload.data.approveUrl;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create PayPal order.";
      setPaymentError(message);
      throw error;
    } finally {
      setPaymentPending(false);
    }
  }, [
    adults,
    apiBaseUrl,
    children,
    email,
    fullName,
    phone,
    phoneCountry,
    startDate,
    endDate,
    travel.id,
    verificationToken,
    whatsApp
  ]);

  const renderAgreementBlock = (block: AgreementBlock, index: number) => {
    if (block.role === "title") {
      return block.lang === "zh" ? (
        <h4 key={`${block.role}-${index}`} className="text-center text-lg font-bold tracking-[0.12em] text-[#111827]">
          {block.text}
        </h4>
      ) : (
        <p
          key={`${block.role}-${index}`}
          className="text-center text-xs uppercase tracking-[0.24em] text-[#c58a00]"
        >
          {block.text}
        </p>
      );
    }

    if (block.role === "party") {
      return (
        <div
          key={`${block.role}-${index}`}
          className="rounded-xl border border-[#eef2f6] bg-[#f8fafc] px-4 py-3 text-xs leading-6 text-[#334155]"
        >
          {block.text}
        </div>
      );
    }

    if (block.role === "chapter") {
      return block.lang === "zh" ? (
        <div key={`${block.role}-${index}`} className="pt-3 text-base font-bold text-[#0f172a]">
          {block.text}
        </div>
      ) : (
        <div key={`${block.role}-${index}`} className="text-xs uppercase tracking-[0.18em] text-[#d38a0c]">
          {block.text}
        </div>
      );
    }

    if (block.role === "article") {
      return block.lang === "zh" ? (
        <div key={`${block.role}-${index}`} className="pt-2 text-sm font-semibold text-[#111827]">
          {block.text}
        </div>
      ) : (
        <div key={`${block.role}-${index}`} className="text-xs font-medium tracking-[0.04em] text-[#64748b]">
          {block.text}
        </div>
      );
    }

    if (block.role === "list") {
      return (
        <div
          key={`${block.role}-${index}`}
          className={`rounded-lg border-l-2 px-3 py-2 ${
            block.lang === "zh"
              ? "border-[#f7b500] bg-[#fffaf0] text-[12px] leading-6 text-[#334155]"
              : "border-[#dbe4ee] bg-white text-[12px] leading-6 text-[#64748b]"
          }`}
        >
          {block.text}
        </div>
      );
    }

    return (
      <p
        key={`${block.role}-${index}`}
        className={
          block.lang === "zh"
            ? "text-[12px] leading-6 text-[#334155]"
            : "text-[12px] leading-6 text-[#64748b]"
        }
      >
        {block.text}
      </p>
    );
  };

  const handleContinueToPayPal = async () => {
    if (paymentPending || !isFormValid) {
      return;
    }

    try {
      const approveUrl = await createPayPalOrder();
      window.location.assign(approveUrl);
    } catch {
      // Error state is already handled in createPayPalOrder.
    }
  };

  return (
    <div className="bg-[#f5f8fb] pb-16 text-[#111827]">
      <section className="mx-auto w-full max-w-[1140px] px-4 pt-8 md:px-8 lg:px-10">
        {onBack && (
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-2 rounded-full bg-[#0b1c2b] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#0d5be1]"
            >
              <span aria-hidden="true">&lt;</span>
              Back to details
            </button>
          </div>
        )}

        <div className="mt-6 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          <div className="space-y-6">
            <div className="rounded-3xl bg-white p-6 shadow-[0_18px_40px_rgba(15,23,36,0.08)]">
              <p className="text-xs uppercase tracking-[0.34em] text-[#f7b500]">
                Booking Summary
              </p>
              <h1 className="mt-3 text-2xl font-semibold text-[#111827] md:text-3xl">
                {travel.basic.title}
              </h1>
              <p className="mt-3 text-sm text-[#6b7280]">
                {travel.basic.destination}
              </p>
              {metaItems.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-3 text-xs text-[#6b7280]">
                  {metaItems.map((item) => (
                    <span
                      key={item.label}
                      className="rounded-full border border-[#e5e7eb] px-3 py-1.5"
                    >
                      <span className="text-[#9aa3af]">{item.label}</span>{" "}
                      <span className="font-semibold text-[#111827]">
                        {item.value}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-3xl bg-white p-6 shadow-[0_18px_40px_rgba(15,23,36,0.08)]">
              <p className="text-xs uppercase tracking-[0.34em] text-[#f7b500]">
                Traveler Details
              </p>
              <h2 className="mt-3 text-xl font-semibold text-[#111827]">
                Basic Information
              </h2>

              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.3em] text-[#ef4444]">
                Required
              </p>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <input
                  type="text"
                  placeholder="Full Name"
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  className="w-full rounded-xl border border-[#e5e7eb] bg-white px-4 py-3 text-sm outline-none"
                />
                <div className="md:col-span-2">
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      resetCodeVerificationState();
                    }}
                    className="w-full rounded-xl border border-[#e5e7eb] bg-white px-4 py-3 text-sm outline-none"
                  />
                  {!isEmailValid && email.length > 0 && (
                    <p className="mt-2 text-xs text-[#ef4444]">
                      Please enter a valid email address.
                    </p>
                  )}
                </div>
                <div className="md:col-span-2 grid gap-3 md:grid-cols-[140px_1fr]">
                  <select
                    value={phoneCountry}
                    onChange={(event) => setPhoneCountry(event.target.value)}
                    className="h-11 w-full rounded-xl border border-[#e5e7eb] bg-white px-3 text-sm outline-none"
                    aria-label="Country code"
                  >
                    {phoneCountryOptions.map(([countryCode, dialCode]) => (
                      <option key={`${countryCode}-${dialCode}`} value={dialCode}>
                        {countryCode} {dialCode}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    className="h-11 w-full rounded-xl border border-[#e5e7eb] bg-white px-4 text-sm outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.28em] text-[#f7b500]">
                    Email Verification
                  </p>
                  <div className="grid gap-3 md:grid-cols-[1fr_auto_auto]">
                    <input
                      type="text"
                      placeholder="Email Verification Code"
                      value={verificationCode}
                      disabled={isCodeVerified}
                      onChange={(event) => {
                        setVerificationCode(event.target.value);
                        resetCodeVerificationState();
                      }}
                      className="w-full rounded-xl border border-[#f7b500]/60 bg-white px-4 py-3 text-sm outline-none focus:border-[#f7b500] focus:ring-2 focus:ring-[#f7b500]/20 disabled:cursor-not-allowed disabled:bg-[#f3f4f6] disabled:text-[#9aa3af]"
                    />
                    <button
                      type="button"
                      onClick={handleSendCode}
                      disabled={isCodeVerified || !isEmailValid || cooldown > 0 || sendCodePending}
                      className="rounded-xl border border-[#f7b500]/60 bg-[#fff7e2] px-4 py-3 text-xs font-semibold text-[#b15c00] transition disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isCodeVerified
                        ? "Verified"
                        : sendCodePending
                        ? "Verifying..."
                        : cooldown > 0
                          ? `Resend in ${cooldown}s`
                          : codeSent
                            ? "Resend Code"
                            : "Send Code"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        void handleVerifyCode();
                      }}
                      disabled={isCodeVerified || !isEmailValid || !isCodeValid || verifyCodePending}
                      className="rounded-xl border border-[#0d5be1]/30 bg-[#eff6ff] px-4 py-3 text-xs font-semibold text-[#0d5be1] transition disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {verifyCodePending
                        ? "Verifying..."
                        : isCodeVerified
                          ? "Verified"
                          : "Verify"}
                    </button>
                  </div>
                  {turnstileVisible && (
                    <div className="mt-3 rounded-xl border border-[#e5e7eb] bg-[#f8fafc] p-3">
                      <p className="text-xs text-[#6b7280]">
                        Complete bot verification to continue.
                      </p>
                      <div id="booking-turnstile" className="mt-3 min-h-[66px]" />
                    </div>
                  )}
                  {sendCodeError && (
                    <p className="mt-2 text-xs text-[#ef4444]">{sendCodeError}</p>
                  )}
                  {sendCodeSuccess && (
                    <p className="mt-2 text-xs text-[#2563eb]">{sendCodeSuccess}</p>
                  )}
                  {verifyCodeError && (
                    <p className="mt-2 text-xs text-[#ef4444]">{verifyCodeError}</p>
                  )}
                  {verifyCodeSuccess && (
                    <p className="mt-2 text-xs text-[#16a34a]">{verifyCodeSuccess}</p>
                  )}
                  {!isCodeValid && verificationCode.length > 0 && (
                    <p className="mt-2 text-xs text-[#ef4444]">
                      Please enter the 6-digit code.
                    </p>
                  )}
                  {codeSent && !sendCodeError && (
                    <p className="mt-2 text-xs text-[#9aa3af]">
                      We have sent a verification code to your email address.
                    </p>
                  )}
                </div>
              </div>

              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.3em] text-[#6b7280]">
                Optional
              </p>
              <div className="mt-3 grid gap-4 md:grid-cols-3">
                <div className="rounded-xl border border-[#e5e7eb] bg-white px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#9aa3af]">
                    WhatsApp{" "}
                    <span className="font-normal text-[#c4c9d1]">(Optional)</span>
                  </p>
                  <input
                    type="text"
                    placeholder="WhatsApp Number"
                    value={whatsApp}
                    onChange={(event) => setWhatsApp(event.target.value)}
                    className="mt-2 w-full text-sm outline-none"
                  />
                </div>
                <div className="rounded-xl border border-[#e5e7eb] bg-white px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#9aa3af]">
                    Adults{" "}
                    <span className="font-normal text-[#c4c9d1]">(Optional)</span>
                  </p>
                  <input
                    type="number"
                    min={1}
                    value={adults}
                    onChange={(event) =>
                      setAdults(Math.max(1, Number(event.target.value)))
                    }
                    className="mt-2 w-full text-sm outline-none"
                  />
                </div>
                <div className="rounded-xl border border-[#e5e7eb] bg-white px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#9aa3af]">
                    Children{" "}
                    <span className="font-normal text-[#c4c9d1]">(Optional)</span>
                  </p>
                  <input
                    type="number"
                    min={0}
                    value={children}
                    onChange={(event) =>
                      setChildren(Math.max(0, Number(event.target.value)))
                    }
                    className="mt-2 w-full text-sm outline-none"
                  />
                </div>
                <div className="rounded-xl border border-[#e5e7eb] bg-white px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#9aa3af]">
                    Start Date{" "}
                    <span className="font-normal text-[#c4c9d1]">(Optional)</span>
                  </p>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className="mt-2 w-full text-sm outline-none"
                  />
                </div>
                <div className="rounded-xl border border-[#e5e7eb] bg-white px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#9aa3af]">
                    End Date{" "}
                    <span className="font-normal text-[#c4c9d1]">(Optional)</span>
                  </p>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className="mt-2 w-full text-sm outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          <aside className="space-y-6 lg:sticky lg:top-24">
            <div className="rounded-3xl bg-white p-6 shadow-[0_18px_40px_rgba(15,23,36,0.12)]">
              <p className="text-xs uppercase tracking-[0.34em] text-[#f7b500]">
                Payment
              </p>
              <h3 className="mt-3 text-xl font-semibold text-[#111827]">
                {bookingCard?.depositTitle ?? "Deposit Payment"}
              </h3>
              <p className="mt-2 text-sm text-[#6b7280]">
                {bookingCard?.depositNote ??
                  "You will be paying a deposit to secure your booking. The remaining balance will be arranged after confirmation."}
              </p>

              <div className="mt-4 rounded-2xl bg-[#0b1c2b] px-5 py-4 text-white">
                <p className="text-2xl font-semibold">
                  {depositAmountLabel}
                </p>
              </div>

              <div className="mt-6 rounded-2xl border border-dashed border-[#e5e7eb] bg-[#f8fafc] p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#6b7280]">
                    Booking Agreement
                  </p>
                  <span className="rounded-full bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#d38a0c]">
                    CN / EN
                  </span>
                </div>
                <div className="max-h-[480px] overflow-y-auto rounded-xl bg-white px-4 py-4 shadow-inner">
                  <div className="space-y-3">
                    {bookingAgreementBlocks.map((block, index) => renderAgreementBlock(block, index))}
                  </div>
                </div>
              </div>

              <label className={`mt-4 flex items-start gap-3 text-xs ${canAgreeToAgreement ? "text-[#5f6b7a]" : "text-[#9aa3af]"}`}>
                <input
                  type="checkbox"
                  checked={agreed}
                  disabled={!canAgreeToAgreement}
                  onChange={(event) => setAgreed(event.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[#d1d5db] disabled:cursor-not-allowed disabled:opacity-60"
                />
                <span>
                  I agree to the booking agreement.
                  {!canAgreeToAgreement && (
                    <span className="mt-1 block text-[#ef4444]">
                      Please fill in Full Name and a valid Email Address before agreeing.
                    </span>
                  )}
                </span>
              </label>

              <div className="mt-6 space-y-3">
                {!paypalEnabled ? (
                  <button
                    type="button"
                    disabled
                    className="w-full rounded-xl bg-[#e5e7eb] px-5 py-3 text-xs font-semibold text-[#6b7280]"
                  >
                    PayPal is not configured
                  </button>
                ) : !isFormValid ? (
                  <button
                    type="button"
                    disabled
                    className="w-full rounded-xl bg-[#e5e7eb] px-5 py-3 text-xs font-semibold text-[#6b7280]"
                  >
                    Complete required fields and verify email to continue
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      void handleContinueToPayPal();
                    }}
                    disabled={paymentPending}
                    className="w-full rounded-xl bg-[#ffc439] px-5 py-3 text-xs font-semibold text-[#111827] transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {paymentPending ? "Redirecting to PayPal..." : "Continue to PayPal"}
                  </button>
                )}

                {paymentPending && (
                  <p className="text-xs text-[#0d5be1]">
                    Redirecting you to PayPal secure checkout...
                  </p>
                )}
                {paymentError && (
                  <p className="text-xs text-[#ef4444]">{paymentError}</p>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
